import React, { Component } from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import PropTypes from "prop-types";
import { Icon, Dimmer, Menu, Loader } from "semantic-ui-react";
import classNames from "classnames";
import Immersive3dTourAppPageStore from "~/stores/Immersive3dTourAppPageStore";
import TourStore from "~/stores/TourStore";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import AddOrRemoveTourModal from "./EditSection/AddOrRemoveTourModal";
import Immersive3dTourExportAdMessageModal from "./EditSection/Immersive3dTourExportAdMessageModal";
import TourJobType from "~/constants/TourJob/TourJobType";
import FloorPlanImportModal from "./FloorPlanImportModal";
import DeviceWidthBreakpoint from "~/constants/DeviceWidthBreakpoint";

@observer
class EditSection extends Component {
  /**
   * @type {null|TourStore}
   */
  @observable tourStore = null;

  constructor(props) {
    super(props);
    const { tourStore } = props;

    this.tourStore = tourStore || new TourStore();

    this.state = {
      addOrRemoveTourModalVisible: false,
      immersive3dTourExportAdMessageModalVisible: false,
      floorPlanImportModalVisible: false,
    };
  }

  /**
   * Has floor plan
   *
   * We keep this for now because we cannot get the hasFloorPlan from the Immersive 3D Tour
   * The Immersive 3D Tour comes from the index endpoint and hasFloorPlan is undefined there
   * @todo Use hasFloorPlan from the API
   * @returns {boolean}
   */
  @computed
  get hasFloorPlan() {
    const { immersive3dTourAppPageStore } = this.props;
    const { immersive3dTourStore, tourJobStore } = immersive3dTourAppPageStore;
    const { immersive3dTour } = immersive3dTourStore;

    return (
      immersive3dTour.isFloorPlanFree ||
      (tourJobStore.immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob &&
        tourJobStore.immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob.typeId ===
          TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN)
    );
  }

  showAddOrRemoveTourModal = () => this.setState({ addOrRemoveTourModalVisible: true });

  hideAddOrRemoveTourModal = () => this.setState({ addOrRemoveTourModalVisible: false });

  showImmersive3dTourExportAdMessageModal = () => this.setState({ immersive3dTourExportAdMessageModalVisible: true });

  hideImmersive3dTourExportAdMessageModal = () => this.setState({ immersive3dTourExportAdMessageModalVisible: false });

  showFloorPlanImportModal = () => this.setState({ floorPlanImportModalVisible: true });

  hideFloorPlanImportModal = () => this.setState({ floorPlanImportModalVisible: false });

  render() {
    const { showError, immersive3dTourAppPageStore, isMyUrbanimmersive } = this.props;
    const {
      addOrRemoveTourModalVisible,
      immersive3dTourExportAdMessageModalVisible,
      floorPlanImportModalVisible,
    } = this.state;

    const {
      immersive3dTourStore,
      immersive3dTourTabStore,
      immersive3dTourFloorPlanImportModalStore,
      immersive3dTourPlayerStore,
    } = immersive3dTourAppPageStore;

    const { immersive3dTour } = immersive3dTourStore;
    const { isTogglingToTour } = immersive3dTourTabStore;
    const { tour, isLoading, tourLoaded } = this.tourStore;

    const addOrRemoveButtonLabel = "Loading...";
    let addOrRemoveButton = <Menu.Item disabled>{addOrRemoveButtonLabel}</Menu.Item>;

    if (!isTogglingToTour) {
      addOrRemoveButton = (
        <Menu.Item
          onClick={() => {
            this.showAddOrRemoveTourModal();
          }}
        >
          {immersive3dTour.isReady && (
            <>
              <Icon name="trash" color="red" /> Remove from Tour
            </>
          )}
          {!immersive3dTour.isReady && (
            <>
              <Icon name="add" color="green" /> Add to Tour
            </>
          )}
        </Menu.Item>
      );
    }

    return (
      <div className="immersive-3d-tour-edition-tab">
        <Dimmer inverted active={immersive3dTourPlayerStore.isLoading || this.tourStore.isLoading}>
          <Loader active>Loading...</Loader>
        </Dimmer>

        <div className={classNames({ hidden: immersive3dTourPlayerStore.isLoading })}>
          <Menu vertical={window.innerWidth < DeviceWidthBreakpoint.SMALL_MONITOR}>
            <Menu.Item
              href={`${window.location.protocol}//${window.location.hostname}/immersive-3d-tours/${immersive3dTour.tourId}/edit`}
              target="_blank"
            >
              <Icon name="external alternate" />
              Edit
            </Menu.Item>
            {!isMyUrbanimmersive && (
              <Menu.Item
                onClick={() => {
                  immersive3dTourTabStore.forceShowUpload = true;
                }}
              >
                <Icon name="add" />
                Add more panoramas
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() => {
                immersive3dTourAppPageStore.goToReplacePanoramaPage();
              }}
            >
              <Icon name="redo" /> Replace panoramas
            </Menu.Item>

            {this.hasFloorPlan && (
              <Menu.Item
                onClick={() => {
                  this.showFloorPlanImportModal();
                }}
              >
                <Icon name="home" />
                Floor plans
              </Menu.Item>
            )}
            {immersive3dTour.isReady && (
              <Menu.Item
                onClick={() => {
                  this.showImmersive3dTourExportAdMessageModal();
                  this.tourStore.load(immersive3dTour.tourId).catch(error => showError(error.message));
                }}
              >
                <Icon name="download" />
                Export
              </Menu.Item>
            )}
            <Menu.Menu position="right">{!isMyUrbanimmersive && addOrRemoveButton}</Menu.Menu>
          </Menu>

          <iframe
            title="Immersive 3D Tour"
            src={`/immersive-3d-tours/${immersive3dTour.tourId}/`}
            className="immersive3dTourEditionPlayer"
          />
        </div>

        <AddOrRemoveTourModal
          visible={addOrRemoveTourModalVisible}
          hideModal={() => this.hideAddOrRemoveTourModal()}
          addToTour={() => immersive3dTourTabStore.addToTour()}
          removeFromTour={() => immersive3dTourTabStore.removeFromTour()}
          immersive3dTour={immersive3dTour}
        />

        <Immersive3dTourExportAdMessageModal
          visible={immersive3dTourExportAdMessageModalVisible}
          hideModal={() => this.hideImmersive3dTourExportAdMessageModal()}
          tour={tour}
          isLoading={isLoading}
          tourLoaded={tourLoaded}
        />

        <FloorPlanImportModal
          immersive3dTourFloorPlanImportModalStore={immersive3dTourFloorPlanImportModalStore}
          immersive3dTourStore={immersive3dTourStore}
          visible={floorPlanImportModalVisible}
          hideModal={() => this.hideFloorPlanImportModal()}
          tourIsPocketWebsite={tour.isPocketWebsite}
        />
      </div>
    );
  }
}

EditSection.propTypes = {
  immersive3dTourAppPageStore: PropTypes.instanceOf(Immersive3dTourAppPageStore),
  isMyUrbanimmersive: PropTypes.bool,
  tourStore: PropTypes.instanceOf(TourStore),
};

export default ErrorWrapper(EditSection);
